import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { ICityDTO } from '@common-frontend-submodule/interfaces/city/icity.interface';
import { ICityCreationDTO } from '@common-frontend-submodule/interfaces/city/icity-creation.interface';
import { ICityUpdateDTO } from '@common-frontend-submodule/interfaces/city/icity-update.interface';
import { IGetCitysRequestDTO } from '@common-frontend-submodule/interfaces/city/iget-citys-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { CityRoutes } from '@common-frontend-submodule/enums/routes/city-routes.enum';

const apiClient = ApiClient.getInstance()

interface CityContextProps {
  getById: (cityId: string) => Promise<IApiResponseGenericDTO<ICityDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<ICityDTO[]>>;
  filter: (model: IGetCitysRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<ICityDTO>>>;
  create: (model: ICityCreationDTO) => Promise<IApiResponseGenericDTO<ICityDTO>>;
  update: (cityId: string, model: ICityUpdateDTO) => Promise<IApiResponseGenericDTO<ICityDTO>>;
  delete: (cityId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const CityContext = createContext<CityContextProps | undefined>(undefined);

export const CityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (cityId: string): Promise<IApiResponseGenericDTO<ICityDTO>> => {
    return await apiClient.getRequest<ICityDTO>(`${CityRoutes.BasePrefix}/${cityId}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<ICityDTO[]>> => {
    return await apiClient.getRequest<ICityDTO[]>(`${CityRoutes.BasePrefix}/${CityRoutes.All}`);
  };

  const filter = async (model: IGetCitysRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<ICityDTO>>> => {
    return await apiClient.postRequest<IGetCitysRequestDTO, IPaginatedResultDTO<ICityDTO>>(
      `${CityRoutes.BasePrefix}/${CityRoutes.List}`,
      model
    );
  };

  const create = async (model: ICityCreationDTO): Promise<IApiResponseGenericDTO<ICityDTO>> => {
    return await apiClient.postRequest<ICityCreationDTO, ICityDTO>(CityRoutes.BasePrefix, model);
  };

  const update = async (cityId: string, model: ICityUpdateDTO): Promise<IApiResponseGenericDTO<ICityDTO>> => {
    return await apiClient.putRequest<ICityUpdateDTO, ICityDTO>(`${CityRoutes.BasePrefix}/${cityId}`, model);
  };

  const deleteCity = async (cityId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${CityRoutes.BasePrefix}/${cityId}`);
  };

  return (
    <CityContext.Provider value={{ getById, getAll, filter, create, update, delete: deleteCity }}>
      {children}
    </CityContext.Provider>
  );
};

export const useCityStore = () => {
  const context = useContext(CityContext);
  if (!context) {
    throw new Error('useCityStore must be used within a CityProvider');
  }
  return context;
};
